import { CachedData } from 'ahooks/lib/useRequest/src/utils/cache'
import { Response } from './Common'
import { DependencyList, MutableRefObject } from 'react'

export type RequestMethod = 'get' | 'GET' | 'POST' | 'post' | 'PUT' | 'put' | 'DELETE' | 'delete'

export type { Response }

export type Service<TData extends { data: any }, TParams extends any[]> = (...args: TParams) => Promise<TData>

export type Subscribe = () => void

export interface StaticOptions {
  manual?: boolean
  onSuccess?: (data: any, ...params: any) => void
  onError?: (e: Error, ...params: any) => void
  onFinally?: (data?: any, e?: Error, ...params: any) => void
  refreshDeps?: DependencyList
  pollingInterval?: number
  cacheKey?: string
  retryCount?: number
  retryInterval?: number
  ready?: boolean
  debounceInterval?: number
}

export type Options<TData extends { data: any }, TParams extends any[], Result = TData['data']> = {
  manual?: boolean
  onBefore?: (params: TParams) => void
  defaultParams?: TParams
  refreshDeps?: DependencyList
  refreshDepsAction?: () => void
  loadingDelay?: number
  pollingInterval?: number
  pollingWhenHidden?: boolean
  pollingErrorRetryCount?: number
  refreshOnWindowFocus?: boolean
  focusTimespan?: number
  debounceWait?: number
  debounceLeading?: boolean
  debounceTrailing?: boolean
  debounceMaxWait?: number
  throttleWait?: number
  throttleLeading?: boolean
  throttleTrailing?: boolean
  cacheKey?: string
  cacheTime?: number
  staleTime?: number
  setCache?: (data: CachedData<TData, TParams>) => void
  getCache?: (params: TParams) => CachedData<TData, TParams> | undefined
  retryCount?: number
  retryInterval?: number
  ready?: boolean
  /**
   * @deprecated
   * 同 debounceWait
   */
  debounceInterval?: number
  onSuccess?: (data: Result, params: TParams) => void
  onError?: (e: Error, params: TParams) => void
  onFinally?: (params: TParams, data?: TData, e?: Error) => void
  formatResult?: (response: TData) => Result
}

export interface FetchState<TData, TParams extends any[]> {
  loading: boolean
  params?: TParams
  data?: TData
  error?: Error
}
export interface PluginReturn<TData extends { data: any }, TParams extends any[]> {
  onBefore?: (params: TParams) =>
    | ({
        stopNow?: boolean
        returnNow?: boolean
      } & Partial<FetchState<TData, TParams>>)
    | void
  onRequest?: (
    service: Service<TData, TParams>,
    params: TParams
  ) => {
    servicePromise?: Promise<TData>
  }
  onSuccess?: (data: TData, params: TParams) => void
  onError?: (e: Error, params: TParams) => void
  onFinally?: (params: TParams, data?: TData, e?: Error) => void
  onCancel?: () => void
  onMutate?: (data: TData) => void
}

export default class Fetch<TData extends { data: any }, TParams extends any[], Result> {
  serviceRef: MutableRefObject<Service<TData, TParams>>
  options: Options<TData, TParams>
  subscribe: Subscribe
  initState: Partial<FetchState<TData, TParams>>
  pluginImpls: PluginReturn<TData, TParams>[]
  count: number
  state: FetchState<TData, TParams>
  constructor(
    serviceRef: MutableRefObject<Service<TData, TParams>>,
    options: Options<TData, TParams>,
    subscribe: Subscribe,
    initState?: Partial<FetchState<TData, TParams>>
  )
  setState(s?: Partial<FetchState<TData, TParams>>): void
  runPluginHandler(event: keyof PluginReturn<TData, TParams>, ...rest: any[]): any
  runAsync(...params: TParams): Promise<Result>
  run(...params: TParams): void
  cancel(): void
  refresh(): void
  refreshAsync(): Promise<TData>
  mutate(data?: TData | ((oldData?: TData) => TData | undefined)): void
}

export interface Result<TData extends { data: any }, TParams extends any[], Result> {
  loading: boolean
  data?: Result
  error?: Error
  params: TParams | []
  cancel: Fetch<TData, TParams, Result>['cancel']
  refresh: Fetch<TData, TParams, Result>['refresh']
  refreshAsync: Fetch<TData, TParams, Result>['refreshAsync']
  run: Fetch<TData, TParams, Result>['runAsync']
  runAsync: Fetch<TData, TParams, Result>['runAsync']
  mutate: Fetch<TData, TParams, Result>['mutate']
}
